import api from './api'
import { useAuth, User } from '@/stores/authStore'

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  password: string
  role: 'CANDIDATE' | 'RECRUITER'
  firstName?: string
  lastName?: string
  companyName?: string
  contactPerson?: string
}

export interface AuthResponse {
  message: string
  user: User
  accessToken: string
  refreshToken: string
}

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/login', credentials)

  // Store auth data
  useAuth.getState().setAuth(
    response.data.user,
    response.data.accessToken,
    response.data.refreshToken
  )

  return response.data
}

/**
 * Register a new user
 */
export async function register(data: RegisterData): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/register', data)

  // Store auth data
  useAuth.getState().setAuth(
    response.data.user,
    response.data.accessToken,
    response.data.refreshToken
  )

  return response.data
}

/**
 * Logout current user
 */
export function logout(): void {
  useAuth.getState().logout()
}

/**
 * Refresh access token
 */
export async function refreshAccessToken(refreshToken: string): Promise<string> {
  const response = await api.post<{ accessToken: string; refreshToken: string }>(
    '/auth/refresh-token',
    { refreshToken }
  )

  // Update access token in store
  useAuth.getState().updateAccessToken(response.data.accessToken)

  return response.data.accessToken
}

/**
 * Resend email verification
 */
export async function resendVerificationEmail(): Promise<{ message: string }> {
  const { user } = useAuth.getState()

  if (!user?.email) {
    throw new Error('No user email found')
  }

  const response = await api.post<{ message: string }>('/auth/send-verification', {
    email: user.email
  })

  return response.data
}
